import React, { useState, useEffect, useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { AuthContext, API } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { toast } from "sonner";
import { format } from "date-fns";
import { 
  Building2, Download, FileText, FileSpreadsheet, Calendar as CalendarIcon,
  LayoutDashboard, Upload, List, ClipboardCheck, FileBarChart, CreditCard, Shield, LogOut, Menu, X
} from "lucide-react";

export default function ReportsPage() {
  const { user, token, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState("all");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    fetchAccounts();
  }, []);

  const fetchAccounts = async () => {
    try {
      const response = await axios.get(`${API}/accounts`, { headers, withCredentials: true });
      setAccounts(response.data);
    } catch (error) {
      console.error("Error fetching accounts:", error);
    }
  };

  const generateReport = async (type, format) => {
    setGenerating(true);
    try {
      const params = new URLSearchParams();
      params.append("format", format);
      if (selectedAccount !== "all") params.append("account_id", selectedAccount);
      if (startDate) params.append("start_date", startDate.toISOString().split('T')[0]);
      if (endDate) params.append("end_date", endDate.toISOString().split('T')[0]);

      const endpoint = type === "summary" ? "summary" : "transactions";
      const response = await axios.get(`${API}/reports/${endpoint}?${params.toString()}`, {
        headers,
        withCredentials: true,
        responseType: format === "csv" ? "blob" : "json"
      });

      if (format === "csv") {
        // Download CSV file
        const blob = new Blob([response.data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${type}_report_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success("Report downloaded successfully");
      } else {
        // Show JSON preview (or you could handle differently)
        console.log("Report data:", response.data);
        toast.success("Report generated - check console for data");
      }
    } catch (error) {
      toast.error("Failed to generate report");
    } finally {
      setGenerating(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const NavLink = ({ to, icon: Icon, children, active }) => (
    <Link to={to} className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${active ? 'bg-zinc-800 text-white' : 'text-zinc-400 hover:text-white hover:bg-zinc-800/50'}`}>
      <Icon className="w-5 h-5" />
      <span className="text-sm font-medium">{children}</span>
    </Link>
  );

  return (
    <div className="min-h-screen bg-[#09090B]">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 bottom-0 w-64 bg-[#121214] border-r border-zinc-800 hidden lg:flex flex-col">
        <div className="p-6 border-b border-zinc-800">
          <Link to="/dashboard" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#FCD34D] flex items-center justify-center">
              <Building2 className="w-6 h-6 text-[#1C1917]" />
            </div>
            <div>
              <h1 className="font-semibold text-white">BankFlow Control</h1>
              <p className="text-xs text-zinc-500">Financial Analysis</p>
            </div>
          </Link>
        </div>
        <nav className="flex-1 p-4 space-y-1">
          <NavLink to="/dashboard" icon={LayoutDashboard}>Dashboard</NavLink>
          <NavLink to="/upload" icon={Upload}>Upload Statements</NavLink>
          <NavLink to="/transactions" icon={List}>Transactions</NavLink>
          <NavLink to="/review" icon={ClipboardCheck}>Review Queue</NavLink>
          <NavLink to="/reports" icon={FileBarChart} active>Reports</NavLink>
          <NavLink to="/accounts" icon={CreditCard}>Bank Accounts</NavLink>
          {user?.role === 'admin' && <NavLink to="/admin" icon={Shield}>Admin Settings</NavLink>}
        </nav>
        <div className="p-4 border-t border-zinc-800">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 rounded-full bg-zinc-800 flex items-center justify-center text-sm font-medium text-white">{user?.name?.charAt(0) || 'U'}</div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.name}</p>
              <p className="text-xs text-zinc-500 truncate">{user?.email}</p>
            </div>
          </div>
          <Button variant="ghost" className="w-full justify-start text-zinc-400 hover:text-white hover:bg-zinc-800" onClick={handleLogout}>
            <LogOut className="w-4 h-4 mr-2" />Sign Out
          </Button>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-[#121214] border-b border-zinc-800">
        <div className="flex items-center justify-between px-4 h-16">
          <Link to="/dashboard" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#FCD34D] flex items-center justify-center">
              <Building2 className="w-5 h-5 text-[#1C1917]" />
            </div>
            <span className="font-semibold text-white">BankFlow</span>
          </Link>
          <Button variant="ghost" size="icon" className="text-zinc-400" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="lg:ml-64 pt-16 lg:pt-0">
        <div className="p-4 md:p-8">
          <div className="mb-8">
            <h1 className="text-2xl md:text-3xl font-bold text-white">Reports</h1>
            <p className="text-zinc-400 text-sm mt-1">Generate and download financial reports</p>
          </div>

          {/* Filters */}
          <Card className="bg-[#121214] border-zinc-800 mb-6">
            <CardHeader>
              <CardTitle className="text-lg text-white">Report Filters</CardTitle>
              <CardDescription className="text-zinc-500">
                Select date range and account to filter reports
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Select value={selectedAccount} onValueChange={setSelectedAccount}>
                  <SelectTrigger className="bg-zinc-900/50 border-zinc-800 text-white" data-testid="report-account-select">
                    <SelectValue placeholder="All Accounts" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121214] border-zinc-800">
                    <SelectItem value="all" className="text-white hover:bg-zinc-800">All Accounts</SelectItem>
                    {accounts.map(acc => (
                      <SelectItem key={acc.account_id} value={acc.account_id} className="text-white hover:bg-zinc-800">{acc.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="justify-start text-left font-normal bg-zinc-900/50 border-zinc-800 text-white hover:bg-zinc-800">
                      <CalendarIcon className="mr-2 h-4 w-4 text-zinc-500" />
                      {startDate ? format(startDate, "PP") : "Start date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-[#121214] border-zinc-800" align="start">
                    <Calendar
                      mode="single"
                      selected={startDate}
                      onSelect={setStartDate}
                      className="bg-[#121214]"
                    />
                  </PopoverContent>
                </Popover>

                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="justify-start text-left font-normal bg-zinc-900/50 border-zinc-800 text-white hover:bg-zinc-800">
                      <CalendarIcon className="mr-2 h-4 w-4 text-zinc-500" />
                      {endDate ? format(endDate, "PP") : "End date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-[#121214] border-zinc-800" align="start">
                    <Calendar
                      mode="single"
                      selected={endDate}
                      onSelect={setEndDate}
                      className="bg-[#121214]"
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </CardContent>
          </Card>

          {/* Report Types */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* Summary Report */}
            <Card className="bg-[#121214] border-zinc-800">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-lg bg-[#FCD34D]/10 flex items-center justify-center">
                    <FileBarChart className="w-6 h-6 text-[#FCD34D]" />
                  </div>
                  <div>
                    <CardTitle className="text-lg text-white">Financial Summary</CardTitle>
                    <CardDescription className="text-zinc-500">
                      Income, expenses, and net position overview
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-zinc-400 mb-4">
                  Includes total income, direct/indirect expenses, gross and net surplus, 
                  breakdowns by account and category.
                </p>
                <div className="flex gap-3">
                  <Button
                    onClick={() => generateReport("summary", "csv")}
                    disabled={generating}
                    className="flex-1 bg-[#FCD34D] text-[#1C1917] hover:bg-[#FBBF24]"
                    data-testid="download-summary-csv"
                  >
                    <FileSpreadsheet className="w-4 h-4 mr-2" />
                    Download CSV
                  </Button>
                  <Button
                    onClick={() => generateReport("summary", "json")}
                    disabled={generating}
                    variant="outline"
                    className="flex-1 border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                    data-testid="download-summary-json"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    View JSON
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Transactions Report */}
            <Card className="bg-[#121214] border-zinc-800">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                    <List className="w-6 h-6 text-emerald-500" />
                  </div>
                  <div>
                    <CardTitle className="text-lg text-white">Transactions Export</CardTitle>
                    <CardDescription className="text-zinc-500">
                      Full transaction listing with categories
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-zinc-400 mb-4">
                  Complete list of all transactions with dates, amounts, categories, 
                  confidence scores, and review status.
                </p>
                <div className="flex gap-3">
                  <Button
                    onClick={() => generateReport("transactions", "csv")}
                    disabled={generating}
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="download-transactions-csv"
                  >
                    <FileSpreadsheet className="w-4 h-4 mr-2" />
                    Download CSV
                  </Button>
                  <Button
                    onClick={() => generateReport("transactions", "json")}
                    disabled={generating}
                    variant="outline"
                    className="flex-1 border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                    data-testid="download-transactions-json"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    View JSON
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Info Card */}
          <Card className="bg-[#121214] border-zinc-800 mt-6">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                  <Download className="w-5 h-5 text-blue-500" />
                </div>
                <div>
                  <h3 className="text-white font-medium mb-1">Export Options</h3>
                  <p className="text-sm text-zinc-400">
                    CSV files can be opened in Excel, Google Sheets, or any spreadsheet application. 
                    JSON format is useful for integrating with other systems or further data processing.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
